import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const CACHE_KEY = 'ecoplanner_vehicles_v1';
const CACHE_TTL = 30 * 1000; // 30s — slightly above polling interval to keep data fresh

function loadCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return [];
    return data;
  } catch { return []; }
}

function saveCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch { /* ignore */ }
}

const MOCK_VEHICLES = [
  { id: 'v1', plate: '00-AA-00', type: 'Camião 7.5t', capacity: '7500 kg', fuelPer100km: 14, position: null },
  { id: 'v2', plate: '11-BB-11', type: 'Camião 12t', capacity: '12000 kg', fuelPer100km: 22, position: null },
  { id: 'v3', plate: '22-CC-22', type: 'Carrinha 3.5t', capacity: '3500 kg', fuelPer100km: 9, position: null },
  { id: 'v4', plate: '33-DD-33', type: 'Camião 18t', capacity: '18000 kg', fuelPer100km: 28, position: null },
  { id: 'v5', plate: '44-EE-44', type: 'Camião 40t', capacity: '40000 kg', fuelPer100km: 35, position: null },
];

export const fetchVehicles = createAsyncThunk("vehicles/fetch", async (token) => {
  const res = await fetch("/planner-api/vehicles", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch vehicles");
  return res.json();
});

// Fetch que inclui veículos desativados (?includeDisabled=true). Usado SÓ por
// componentes que mostram a frota a nível visual (ecrã Frota, mapa) — os
// desativados não entram em operações. O resultado vai para state.fullList
// e não toca em state.list, que continua a ser "só ativos" (servido pelo
// polling + fetchVehicles e consumido pelo resto da app: atribuir OT, etc.).
export const fetchAllVehicles = createAsyncThunk("vehicles/fetchAll", async (token) => {
  const res = await fetch("/planner-api/vehicles?includeDisabled=true", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch all vehicles");
  return res.json();
});

export const createVehicle = createAsyncThunk("vehicles/create", async ({ token, data }) => {
  const res = await fetch("/planner-api/vehicles", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.error || "Erro ao criar veículo");
  return result;
});

export const updateVehicle = createAsyncThunk("vehicles/update", async ({ token, id, data }) => {
  const res = await fetch(`/planner-api/vehicles/${id}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.error || "Erro ao atualizar veículo");
  return result;
});

export const deleteVehicle = createAsyncThunk("vehicles/delete", async ({ token, id }) => {
  const res = await fetch(`/planner-api/vehicles/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const result = await res.json();
    throw new Error(result.error || "Erro ao eliminar veículo");
  }
  return id;
});

const vehiclesSlice = createSlice({
  name: "vehicles",
  // `list` = só ativos (consumido por toda a app operacional).
  // `fullList` = ativos + desativados (consumido SÓ pelo ecrã Frota e mapa
  // para mostrarem os desativados a título visual). Os polls/WS atualizam só
  // `list`; `fullList` é refrescado quando o ecrã Frota/mapa dispara o thunk.
  initialState: { list: loadCached(), fullList: [], loading: false, loadingFull: false },
  reducers: {
    loadMockVehicles: (state) => {
      state.list = MOCK_VEHICLES;
      state.loading = false;
    },
    setVehiclesFromWS: (state, action) => {
      if (Array.isArray(action.payload) && action.payload.length > 0) {
        // Preserve simulation state across Traccar polling refreshes — without
        // this, every WS poll replaces the list wholesale, wiping
        // `_simulating` and the simulated position. The live panel then sees
        // the real GPS coords come back and pans the map to them, fighting
        // the simulator's own position updates.
        const simIndex = new Map();
        for (const v of state.list) {
          if (v._simulating) simIndex.set(v.id ?? v.plate, v);
        }
        state.list = action.payload.map(v => {
          const sim = simIndex.get(v.id) || simIndex.get(v.plate);
          if (!sim) return v;
          // Keep the simulator-driven position + flags; merge in any new
          // metadata from Traccar (driver name, speed limit, etc.) that
          // doesn't conflict with the simulation.
          return {
            ...v,
            position: sim.position,
            _simulating: true,
            _simProgress: sim._simProgress,
            _simRouteId: sim._simRouteId,
          };
        });

        // Aplicar as posições frescas TAMBÉM ao `fullList` (ativos+desativados).
        // O mapa de Planeamento e a Frota leem `selectAllVehicles` (=fullList);
        // sem isto, assim que o fetchAllVehicles corre e preenche o fullList, as
        // viaturas CONGELAM nas posições desse snapshot e deixam de se mexer no
        // mapa (o WS só atualizava o `list`). Preserva os desativados (ausentes
        // no payload do WS) e a simulação em curso.
        if (Array.isArray(state.fullList) && state.fullList.length > 0) {
          const freshById = new Map();
          for (const v of action.payload) {
            if (v.id != null) freshById.set(String(v.id), v);
            if (v.plate) freshById.set(String(v.plate), v);
          }
          state.fullList = state.fullList.map(v => {
            if (v._simulating) return v; // a simulação manda na posição
            const fresh = freshById.get(String(v.id)) || freshById.get(String(v.plate));
            return fresh ? { ...v, ...fresh } : v;
          });
        }

        state.loading = false;
        saveCache(action.payload);
      }
    },
    updateSimulatedVehicle: (state, action) => {
      const { vehicleId, plate, position, progress, routeId } = action.payload;
      const idx = state.list.findIndex(v => v.id === vehicleId || v.plate === plate);
      if (idx >= 0) {
        // Update existing vehicle position
        state.list[idx].position = {
          ...state.list[idx].position,
          latitude: position.latitude,
          longitude: position.longitude,
          speed: position.speed,
          heading: position.heading,
        };
        state.list[idx]._simulating = true;
        state.list[idx]._simProgress = progress;
        // Track which route this vehicle is simulating — needed by the OT row's
        // progress bar so it only picks up vehicles tied to the current OT.
        if (routeId != null) state.list[idx]._simRouteId = routeId;
      } else {
        // Insert simulated vehicle
        state.list.push({
          id: vehicleId,
          plate: plate || 'SIM-001',
          type: 'truck',
          model: 'Simulação',
          position: {
            latitude: position.latitude,
            longitude: position.longitude,
            speed: position.speed || 0,
            heading: position.heading || 0,
          },
          _simulating: true,
          _simProgress: progress,
          _simRouteId: routeId,
        });
      }
      // Espelhar a simulação no `fullList` — o mapa de Planeamento lê
      // selectAllVehicles (=fullList) quando este está preenchido, por isso a
      // viatura simulada tem de se mover lá também, senão fica invisível/parada.
      if (Array.isArray(state.fullList) && state.fullList.length > 0) {
        const fIdx = state.fullList.findIndex(v => v.id === vehicleId || v.plate === plate);
        if (fIdx >= 0) {
          state.fullList[fIdx] = {
            ...state.fullList[fIdx],
            position: {
              ...state.fullList[fIdx].position,
              latitude: position.latitude, longitude: position.longitude,
              speed: position.speed, heading: position.heading,
            },
            _simulating: true, _simProgress: progress,
            ...(routeId != null ? { _simRouteId: routeId } : {}),
          };
        } else {
          const simV = state.list.find(v => v.id === vehicleId || v.plate === plate);
          if (simV) state.fullList.push(simV);
        }
      }
    },
    clearSimulatedVehicle: (state, action) => {
      const idx = state.list.findIndex(v => v._simulating && (v.id === action.payload || v.plate === action.payload));
      if (idx >= 0) {
        state.list[idx]._simulating = false;
        state.list[idx]._simProgress = null;
      }
      const fIdx = state.fullList.findIndex(v => v._simulating && (v.id === action.payload || v.plate === action.payload));
      if (fIdx >= 0) {
        state.fullList[fIdx]._simulating = false;
        state.fullList[fIdx]._simProgress = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVehicles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.list = action.payload;
        state.loading = false;
        saveCache(action.payload);
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.loading = false;
        console.error("fetchVehicles failed:", action.error?.message);
      })
      .addCase(fetchAllVehicles.pending, (state) => {
        state.loadingFull = true;
      })
      .addCase(fetchAllVehicles.fulfilled, (state, action) => {
        state.fullList = action.payload;
        state.loadingFull = false;
      })
      .addCase(fetchAllVehicles.rejected, (state, action) => {
        state.loadingFull = false;
        console.error("fetchAllVehicles failed:", action.error?.message);
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })
      .addCase(updateVehicle.fulfilled, (state, action) => {
        const idx = state.list.findIndex((v) => v.id === action.payload.id);
        if (idx >= 0) state.list[idx] = action.payload;
      })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.list = state.list.filter((v) => v.id !== action.payload);
      });
  },
});

export const { loadMockVehicles, setVehiclesFromWS, updateSimulatedVehicle, clearSimulatedVehicle } = vehiclesSlice.actions;
export const selectVehicles = (state) => state.vehicles.list;
// `selectAllVehicles` inclui ativos + desativados — usar SÓ no ecrã Frota
// e mapa para mostrarem os desativados a título visual.
export const selectAllVehicles = (state) =>
  state.vehicles.fullList && state.vehicles.fullList.length > 0
    ? state.vehicles.fullList
    : state.vehicles.list; // fallback enquanto o fetchAllVehicles ainda não correu
export const selectVehicleById = (id) => (state) =>
  state.vehicles.list.find((v) => v.id === id);
export default vehiclesSlice.reducer;
